import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import InventoryPicker from '../InventoryPicker'
import { ioSend, ioSendForm } from './api'
import { btnPrimary, btnSecondary, cardClass, inputClass } from './ui'

const STEPS = ['Campaign', 'Line order', 'Creatives', 'Review']

function emptyLine() {
  return {
    id: `lo-${Date.now()}`,
    name: 'Line order 1',
    dma: '',
    cbsa: '',
    daypart: 'all-day',
    displayUnitIds: [],
    startDate: '',
    endDate: '',
  }
}

export default function AudienceCampaignWizard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [advertiserName, setAdvertiserName] = useState(user?.companyName || '')
  const [asProposal, setAsProposal] = useState(true)
  const [lines, setLines] = useState([emptyLine()])
  const [activeLine, setActiveLine] = useState(0)
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const line = lines[activeLine] || lines[0]

  const updateLine = (patch) => {
    setLines((prev) => prev.map((l, i) => i === activeLine ? { ...l, ...patch } : l))
  }

  const submit = async (convert) => {
    if (!name.trim()) {
      setError('Campaign name is required')
      return
    }
    setBusy(true)
    setError('')
    const lineItems = lines.map((l) => ({
      type: 'audience',
      name: l.name,
      displayUnitIds: l.displayUnitIds,
      dma: l.dma,
      cbsa: l.cbsa,
      daypart: l.daypart,
      flight: { startDate: l.startDate, endDate: l.endDate },
    }))
    const ioAction = convert || !asProposal ? 'submit' : 'draft'
    try {
      if (files.length) {
        const fd = new FormData()
        fd.append('advertiserName', advertiserName || 'Advertiser')
        fd.append('contactEmail', user?.mail || '')
        fd.append('campaignName', name.trim())
        fd.append('adType', 'audience')
        fd.append('recordKind', 'audience')
        fd.append('ioAction', ioAction)
        if (line?.startDate) fd.append('startDate', line.startDate)
        if (line?.endDate) fd.append('endDate', line.endDate)
        fd.append('formDataJson', JSON.stringify({
          lineItems,
          selectedDisplayUnitIds: lines.flatMap((l) => l.displayUnitIds || []),
          ioAction,
          asProposal: asProposal && !convert,
        }))
        files.forEach((f) => fd.append('files', f))
        if (files[0]) {
          fd.append('fileMetadata', JSON.stringify({
            0: { alt: name.trim() || 'Audience creative' },
            [files[0].name]: { alt: name.trim() || 'Audience creative' },
          }))
        }
        const result = await ioSendForm('/api/orders', fd)
        navigate(`/campaigns/${result.id}`)
      } else {
        const result = await ioSend('/api/io/campaigns', 'POST', {
          recordKind: 'audience',
          ioAction,
          campaignName: name.trim(),
          advertiserName: advertiserName || 'Advertiser',
          contactEmail: user?.mail,
          startDate: line?.startDate,
          endDate: line?.endDate,
          lineItems,
          selectedDisplayUnitIds: lines.flatMap((l) => l.displayUnitIds || []),
          formData: { asProposal: asProposal && !convert },
        })
        navigate(`/campaigns/${result.id}`)
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Audience campaign</h1>
        <p className="text-sm text-gray-500">Plan line orders against live inventory, save as a proposal, then convert for booking.</p>
      </div>
      <ol className="flex gap-2">
        {STEPS.map((label, i) => (
          <li key={label} className={`flex-1 text-center text-xs font-medium py-2 rounded-lg ${
            i === step ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
          }`}>{i + 1}. {label}</li>
        ))}
      </ol>
      <div className={`${cardClass} p-6 space-y-4`}>
        {step === 0 && (
          <>
            <input className={inputClass} placeholder="Campaign name *" value={name} onChange={(e) => setName(e.target.value)} />
            <input className={inputClass} placeholder="Advertiser" value={advertiserName} onChange={(e) => setAdvertiserName(e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={asProposal} onChange={(e) => setAsProposal(e.target.checked)} />
              Save as proposal first
            </label>
          </>
        )}
        {step === 1 && (
          <>
            <div className="flex flex-wrap gap-2">
              {lines.map((l, i) => (
                <button key={l.id} type="button" className={i === activeLine ? btnPrimary : btnSecondary} onClick={() => setActiveLine(i)}>
                  {l.name}
                </button>
              ))}
              <button type="button" className={btnSecondary} onClick={() => {
                const next = { ...emptyLine(), id: `lo-${Date.now()}`, name: `Line order ${lines.length + 1}` }
                setLines([...lines, next])
                setActiveLine(lines.length)
              }}>+ Line order</button>
            </div>
            <input className={inputClass} value={line.name} onChange={(e) => updateLine({ name: e.target.value })} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className={inputClass} placeholder="DMA markets" value={line.dma} onChange={(e) => updateLine({ dma: e.target.value })} />
              <input className={inputClass} placeholder="CBSA markets" value={line.cbsa} onChange={(e) => updateLine({ cbsa: e.target.value })} />
              <select className={inputClass} value={line.daypart} onChange={(e) => updateLine({ daypart: e.target.value })}>
                <option value="all-day">All day</option>
                <option value="am">AM</option>
                <option value="pm">PM</option>
                <option value="evening">Evening</option>
              </select>
              <input type="date" className={inputClass} value={line.startDate} onChange={(e) => updateLine({ startDate: e.target.value })} />
              <input type="date" className={inputClass} value={line.endDate} onChange={(e) => updateLine({ endDate: e.target.value })} />
            </div>
            <InventoryPicker
              selectedIds={line.displayUnitIds}
              onChange={(ids) => updateLine({ displayUnitIds: ids })}
              title="Screens for this line order"
              subtitle="Dynamic pool from live inventory"
            />
          </>
        )}
        {step === 2 && (
          <>
            <p className="text-sm text-gray-500">Each line order needs at least one creative before booking.</p>
            <input type="file" accept="image/*,video/mp4" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            {files.length > 0 && <p className="text-xs text-gray-500">{files.map((f) => f.name).join(', ')}</p>}
          </>
        )}
        {step === 3 && (
          <dl className="text-sm space-y-2">
            <div><span className="text-gray-500">Name: </span>{name}</div>
            <div><span className="text-gray-500">Line orders: </span>{lines.length}</div>
            <div><span className="text-gray-500">Faces: </span>{lines.reduce((n, l) => n + (l.displayUnitIds?.length || 0), 0)}</div>
            <div><span className="text-gray-500">Creatives: </span>{files.length}</div>
            <div><span className="text-gray-500">Saved as: </span>{asProposal ? 'proposal (draft)' : 'campaign (submit)'}</div>
          </dl>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-between">
          <button type="button" className={btnSecondary} disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)}>Back</button>
          {step < 3 ? (
            <button type="button" className={btnPrimary} onClick={() => setStep((s) => s + 1)}>Next</button>
          ) : (
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} disabled={busy} onClick={() => submit(false)}>
                {asProposal ? 'Save proposal' : 'Create campaign'}
              </button>
              {asProposal && (
                <button type="button" className={btnPrimary} disabled={busy} onClick={() => submit(true)}>
                  Convert to campaign
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function AudienceHome() {
  const navigate = useNavigate()
  useEffect(() => {
    navigate('/campaigns?kind=audience', { replace: true })
  }, [navigate])
  return <p className="text-gray-500">Opening audience campaigns…</p>
}
